import { CommonModule, KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil, finalize, Subject, tap } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { SettingsService } from 'src/app/services/settings.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RenderFormat, IReportParameters } from '../reports-models';
import { ReportsService } from '../reports.service';
import { LocalizationService } from 'src/app/shared/localization/localization.service';
import { ReportsUtilsService } from '../reports-utils.service';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { FormsModule } from '@angular/forms';
@Component({
  selector: 'app-success-rate',
  templateUrl: './success-rate.component.html',
  styleUrls: ['./success-rate.component.scss'],
  imports: [CommonModule, TranslateModule, CustomMultiSelectComponent, CustomSelectComponent, FormsModule]
})
export class SuccessRateComponent implements OnInit, OnDestroy {
  selectedPeriod = -1;
  periodOptions: KeyValue<any, string>[];
  userAssignedCustomersCount: number;
  debtorTypeOptions: KeyValue<string, string>[];
  customers$ = this.appService.customersSelectItems$
    .pipe(tap(_ => {
      this.userAssignedCustomersCount = _.length;
      if (this.userAssignedCustomersCount == 1)
        {this.selectedCustomers = [_[0].key]; }
    }));
  selectedCustomers: number[];
  selectedDebtorType = 'A';
  includeReminderCases = false;
  renderedReport: any;
  showResults: boolean;

  private readonly unsubscribe$ = new Subject();
  private reportFolder: string;
  private language: string;

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private localization: LocalizationService,
    private spinnerService: GlobalSpinnerService,
    private utilsService: UtilsService,
    private _sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private notificationService: MessageToUiService,
    private settingsService: SettingsService,
    private reportUtils: ReportsUtilsService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.debtorTypeOptions = this.reportUtils.getDebtorTypeOptions();
    this.periodOptions = this.reportUtils.getYearlyPeriodOptions();
    this.settingsService.getSettingValueByName('ReportServer_FolderForKreditorWeb')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(settingValue => this.reportFolder = settingValue);

    this.localization.currentLanguage$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => this.language = result);
  }

  onGenerateReport() {
    const parameters = this.getReportParameters();

    this.spinnerService.show();

    this.reportsService.renderReport(parameters)
      .pipe(finalize(() => {
        this.spinnerService.hide();
      }))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          const blobData = this.utilsService.b64toBlob(result.data, 'application/pdf');
          this.renderedReport = this._sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blobData));
          this.showResults = true;
        },
        error: () => this.notificationService.genericError()
      });
  }

  private getReportParameters(): IReportParameters {
    const parameters = {
      templatePath: `${this.reportFolder}SuccessRate_Monthly`,
      displayName: this.translateService.instant('Reports.SuccessRate.Title'),
      format: RenderFormat.Pdf,
      parameters: {
        Language: this.language.toLocaleLowerCase(),
        DebtorIsPerson: this.selectedDebtorType == 'A' ? null : (this.selectedDebtorType == 'P').toString(),
        IncludeReminderCases: this.includeReminderCases.toString(),
        Year: this.selectedPeriod.toString()
      }
    } as IReportParameters;
    if (this.selectedCustomers.length > 0) {
      parameters.parameters.CustomerNumbers = this.selectedCustomers?.join(',').toString();
    }
    return parameters;
  }
}
